import { parse, isValid, format } from 'date-fns';
import { onAccountingChanged } from './accounting/accountingConnectService';
import { CLEAR_DATE_VALUE, CLEAR_NUMBER_VALUE } from './matterPayloadUtils';
angular.module('leanlaw.core').factory('clientMatterManager', ['$q', 'leanLawAuth', 'leanLawApi', 'accountingService', 'userService', 'onboardService', '$http', function ($q, leanLawAuth, leanLawApi, accountingService, userService, onboardService, $http) {
  const _state = {};
  const domain = `${leanLawAuth.domain}/v1`;
  refresh();
  function refresh() {
    _state.clientMapPromise = null; // promise({ id -> Client })
    _state.matterMapPromise = null; // promise({ id -> Matter }) - includes ALL matters (primary + secondary)

    _state.scopeLoadedMap = {}; // { "scopeAccount|scope" -> true }  - true if scopeAccount|scope combo is loaded into matterMap
    _state.clientLists = {}; // { optionKey -> [Client] }

    _state.tabledClientLists = []; // { optionKey -> [Client] }

    _state.matterLists = {}; // { optionKey -> [Matter] }
  }

  // A connect performed outside this service still changes what our maps hold.
  onAccountingChanged(({
    clients,
    matters
  }) => invalidate(clients, matters));
  userService.subscribe('all', () => {
    invalidate(true, true);
  });
  function optionKey(options = {}) {
    return `${options.scope || 'involved'}|${options.scopeAccount || 'me'}|${options.matters || 'include'}|${options.billingType || 'all'}|${options.status || 'active'}|${options.showSecondarySplitMatter || 'false'}`;
  }
  function includeMatter(matter, me, options) {
    const includeActive = options.status !== 'archived';
    if (matter.active && !includeActive) {
      return false;
    }
    const includeArchived = options.status === 'archived' || options.status === 'all';
    if (!matter.active && !includeArchived) {
      return false;
    }

    // Filter out secondary split matters unless explicitly requested
    if (matter.isSecondarySplitMatter && !options.showSecondarySplitMatter) {
      return false;
    }
    const key = mapKeyFromOptions(me, options);
    if (key) {
      return matter.$access[key];
    }
    if (options.scope === 'involved' || !options.scope) {
      return matter.access.involved;
    }
    if (options.scope === 'access') {
      return matter.access.access;
    }
    if (options.scope && options.scope.indexOf('primary') >= 0) {
      return matter.access.primary;
    }
    return matter.$access.me;
  }
  function Client() {}
  Client.prototype.load = function (json, detail) {
    const client = this;
    Object.assign(client, json);
    client.$original = json;
    client.$detail = detail;
    return $q.resolve(this);
  };
  Client.prototype.update = function (update) {
    const updating = this;
    return loadClientMap().then(clientMap => {
      // we return a copy of client from the clientMap when doing listClients so
      // so we need to find the original in case use is calling update on an Object
      // from listClients as opposed to one from getClient
      // clientMap only holds {scope:'all', status:'active'} clients, so an archived
      // client (or one getClient loaded without hydrating the map) won't be in it.
      const client = clientMap[updating.id] || updating;
      return leanLawApi.updateClient({
        id: client.id,
        body: update || updating
      }).then(updated => {
        invalidate(true, false);
        return client.load(updated, true);
      });
    });
  };
  Client.prototype.undo = function () {
    const client = this;
    Object.assign(client, client.$original);
  };
  Client.prototype.delete = function () {
    const client = this;
    return leanLawApi.deleteClient({
      id: client.id
    }).then(() => {
      // We don't have to worry about the deleted client having matters because
      // it is not allowed by backend to remove client with any matters
      invalidate(true, false);
      return loadClientMap().then(clientMap => {
        delete clientMap[client.id];
      });
    });
  };
  Client.prototype.getOrConnectAccounting = function () {
    const client = this;
    if (client.accounting) {
      return $q.resolve({
        client: client.accounting,
        trust: client.billing.trust
      });
    }
    return accountingService.verify().then(status => userService.me().then(me => {
      const clientBilling = me.company.settings.clientBilling || {};
      if (clientBilling.allowChange) {
        return {
          connect: true
        };
      }
      return accountingService.updateClient(client.id, client.label, clientBilling.billClient).then(updated => {
        invalidate(true, false);
        return client.load(updated, true).then(c => ({
          client: c.accounting,
          trust: c.billing.trust
        }));
      }).catch(() => ({
        connect: true
      }));
    })).catch(status => {
      if (!status.enabled) {
        return {
          connect: false
        };
      }
      return $q.reject('QuickBooks is not connected');
    });
  };
  function Matter() {}

  // Parses a YYYY-MM-DD string as local midnight to avoid the UTC offset shift
  // that new Date("YYYY-MM-DD") causes in US timezones (same pattern as getShortDateString).
  function parseLocalDate(str) {
    if (!str) return null;
    if (str instanceof Date) return isValid(str) ? str : null;
    const d = parse(str, 'yyyy-MM-dd', new Date());
    return isValid(d) ? d : new Date(str);
  }
  Matter.prototype.load = function (json, detail) {
    const matter = this;
    Object.assign(matter, json);
    matter.$original = angular.copy(json);
    matter.$detail = detail;
    if (matter.users) {
      matter.$access = matter.$access || {};
      matter.$access.me = true;
      matter.users.forEach(user => {
        matter.$access[user.id] = true;
      });
    }
    matter.opened = parseLocalDate(matter.opened);
    matter.activity = matter.lastActivity ? new Date(matter.lastActivity) : matter.opened;

    // Convert contingency date fields from ISO strings to Date objects
    if (matter.expectedSettlementDate) {
      matter.expectedSettlementDate = parseLocalDate(matter.expectedSettlementDate);
    }
    if (matter.settlementDate) {
      matter.settlementDate = parseLocalDate(matter.settlementDate);
    }
    if (matter.billing?.originationStepDownStart) {
      matter.billing.originationStepDownStart = parseLocalDate(matter.billing.originationStepDownStart);
    }
    matter.billingType = matter.billingType || 'Unknown';
    if (matter.billing?.type === 'hours') matter.billingType = 'Hourly';
    if (matter.billing?.type === 'none') matter.billingType = 'Pro Bono';
    if (matter.billing?.type === 'contingency') matter.billingType = 'Contingency';
    if (matter.billing?.type === 'fixed') matter.billingType = 'Fixed Fee';
    if (matter.billing?.type === 'internal') matter.billingType = 'Internal';
    return userService.list({
      status: 'registered'
    }).then(users => {
      (matter.users || []).forEach(matterUser => {
        const user = users.find(u => u.id === matterUser.id);
        matterUser.name = user.name;
        matterUser.rate = matterUser.rate || user.standardRate;
      });
      return matter;
    });
  };
  Matter.prototype.update = function (update, updateEntries) {
    return userService.me().then(me => {
      const matter = this;
      if (update) {
        return updateMatter(this, update);
      }

      // Helper to check if a date is valid
      const isValidDate = value => {
        if (value == null) return false;
        const date = value instanceof Date ? value : new Date(value);
        return !isNaN(date.getTime());
      };

      // Validate required date field - opened must be a valid date
      if (!isValidDate(matter.opened)) {
        return $q.reject('Date Opened is required and must be a valid date');
      }
      const hasPrimary = matter.users && matter.users.find(u => u.primary);
      if (!hasPrimary && matter.restricted) {
        return $q.reject(`${me.terms.Matter}s must have a 'Responsible' assigned`);
      }

      // Transform cleared contingency field values to sentinel values per backend contract.
      // Sending a clear sentinel for an already-null field is a safe no-op.
      const transformDate = value => {
        // Invalid or null/undefined → send clear sentinel; valid date → send as YYYY-MM-DD
        if (value == null || !isValidDate(value)) return CLEAR_DATE_VALUE;
        const date = value instanceof Date ? value : new Date(value);
        return format(date, 'yyyy-MM-dd');
      };
      const transformNumber = value => {
        // null/undefined/empty → send -1 sentinel; 0 and other numbers are valid
        if (value == null || value === '') return CLEAR_NUMBER_VALUE;
        return value;
      };
      const transformEnum = value => {
        // null/undefined → send "" to clear; string value → send as-is
        if (value == null || value === '') return '';
        return value;
      };

      // Transform object dropdowns (like matterTag) - need { id: null } to clear
      const transformObjectDropdown = value => {
        if (value == null || value.id == null) return {
          id: null
        };
        return value;
      };

      // Transform text fields - null/undefined → "" to clear
      const transformText = value => {
        if (value == null) return '';
        return value;
      };

      // Transform custom object - ensure all text fields send "" when cleared
      const transformedCustom = matter.custom ? {
        department: transformText(matter.custom.department),
        practiceArea: transformText(matter.custom.practiceArea),
        referralSource: transformText(matter.custom.referralSource),
        adverse: transformText(matter.custom.adverse),
        relatedAdverse: transformText(matter.custom.relatedAdverse),
        relatedClients: transformText(matter.custom.relatedClients),
        opposingCounsel: transformText(matter.custom.opposingCounsel),
        instructions: transformText(matter.custom.instructions),
        email: transformText(matter.custom.email),
        emailCC: transformText(matter.custom.emailCC)
      } : matter.custom;

      // Transform billing object - handle dates and numbers inside it
      const transformedBilling = matter.billing ? {
        ...matter.billing,
        originationStepDownStart: transformDate(matter.billing.originationStepDownStart),
        originationStepDownPeriodInMonths: transformNumber(matter.billing.originationStepDownPeriodInMonths)
      } : matter.billing;
      return updateMatter(matter, {
        billing: transformedBilling,
        client: {
          id: matter.client.id
        },
        matterTag: transformObjectDropdown(matter.matterTag),
        custom: transformedCustom,
        name: matter.name,
        opened: matter.opened instanceof Date ? format(matter.opened, 'yyyy-MM-dd') : matter.opened,
        restricted: matter.restricted,
        users: matter.users,
        reference: transformText(matter.reference),
        description: transformText(matter.description),
        externalId: transformText(matter.externalId),
        ledesId: transformText(matter.ledesId),
        ledesInvoiceDescription: transformText(matter.ledesInvoiceDescription),
        contact: matter.contact,
        rateOptions: {
          updateEntries
        },
        // Contingency fields - transform cleared values to sentinels
        caseStatus: transformEnum(matter.caseStatus),
        marketingSource: transformEnum(matter.marketingSource),
        expectedSettlementAmount: transformNumber(matter.expectedSettlementAmount),
        expectedCaseCosts: transformNumber(matter.expectedCaseCosts),
        expectedSettlementDate: transformDate(matter.expectedSettlementDate),
        settlementDate: transformDate(matter.settlementDate)
      });
    });
  };
  function updateMatter(matter, update) {
    return leanLawApi.updateMatter({
      id: matter.id,
      body: update
    }).then(updated => {
      invalidate(false, true);
      return matter.load(updated, true);
    });
  }
  Matter.prototype.activate = function (active) {
    const matter = this;
    return leanLawApi.updateMatter({
      id: matter.id,
      body: {
        active
      }
    }).then(updated => {
      invalidate(true, true);
      return matter.load(updated, true);
    });
  };
  Matter.prototype.delete = function (active) {
    // Removed cached matters
    const matter = this;
    return leanLawApi.deleteMatter({
      id: matter.id
    }).then(() => loadMatterMap().then(matterMap => {
      delete matterMap[matter.id];
      invalidate(true, true);
    }));
  };
  Matter.prototype.assign = function () {
    const matter = this;
    return leanLawApi.assignMatter({
      id: matter.id
    }).then(updated => {
      invalidate(false, true);
      return matter.load(updated);
    });
  };
  Matter.prototype.stats = function () {
    const matter = this;
    return leanLawApi.getMatterStats({
      id: matter.id
    });
  };
  Matter.prototype.reload = function () {
    return this.load(this.$original);
  };
  Matter.prototype.testAccess = function () {
    const matter = this;
    if (this.active && this.involved) {
      return $q.resolve();
    }
    const status = [];
    const resolution = [];
    if (!matter.active) {
      status.push('is archived');
      resolution.push('activate');
    }
    if (!matter.involved) {
      if (matter.restricted) {
        status.push('has restricted access');
        resolution.push('request access to');
      } else {
        status.push('does not have you assigned');
        resolution.push('add yourself to');
      }
    }
    return userService.me().then(me => {
      let prompt = '';
      if (status.length > 0) {
        prompt += `<p>This ${me.terms.matter} ${status.join(' and ')}.</p>`;
      }
      prompt += '<p>';
      if (resolution.length > 0) {
        prompt += `Would you like to ${resolution.join(' and ')}`;
      } else {
        prompt += 'Use';
      }
      prompt += ` the ${me.terms.matter}?</p>`;
      return $q.reject(prompt);
    });
  };
  Matter.prototype.getOrConnectAccounting = function () {
    return userService.me().then(me => {
      const matter = this;
      if (matter.accounting) {
        return accountingInfo(matter);
      }
      if (matter.client.billing.billClient && matter.client.accounting) {
        return accountingInfo(null, matter.client);
      }
      return accountingService.verify().then(status => {
        if (matter.client.accounting) {
          return updateMatterAccounting(matter);
        }
        const clientBilling = me.company.settings.clientBilling || {};
        if (clientBilling.allowChange) {
          return {
            connect: true
          };
        }
        if (clientBilling.billClient && matter.client.billing.billClient) {
          return updateClientAccounting(matter.client, true).catch(() => ({
            connect: true
          }));
        }
        return updateClientAccounting(matter.client).then(() => updateMatterAccounting(matter));
      }).catch(status => {
        if (!status.enabled) {
          return {
            connect: false
          };
        }
        return $q.reject('QuickBooks is not connected');
      });
    });
  };
  function updateMatterAccounting(matter) {
    return accountingService.updateMatter(matter.id, matter.label).then(updated => {
      invalidate(false, true);
      return matter.load(updated, true).then(m => accountingInfo(matter));
    }).catch(err => ({
      connect: true
    }));
  }
  function updateClientAccounting(client, billClient) {
    return accountingService.updateClient(client.id, client.label, billClient).then(updated => {
      invalidate(true, false);
      client.accounting = updated.accounting;
      client.billing = updated.billing;
      return accountingInfo(null, client);
    });
  }
  function accountingInfo(matter, client) {
    return $q.resolve({
      matter: matter ? matter.accounting : null,
      client: client ? client.accounting : matter.client.accounting,
      trust: matter ? matter.billing.trust : client.billing.trust
    });
  }
  function listClients(options = {}) {
    return userService.me().then(me => {
      const key = optionKey(options);
      if (!_state.clientLists[key]) {
        _state.clientLists[key] = loadClientMap().then(clientMap => loadMatterMap(options).then(matterMap => {
          // Index matters by client once, rather than scanning every matter
          // per client (O(clients x matters) -> O(matters)).
          const mattersByClientId = {};
          Object.keys(matterMap).forEach(matterId => {
            const matter = matterMap[matterId];
            const clientMatters = mattersByClientId[matter.client.id] || [];
            clientMatters.push(matter);
            mattersByClientId[matter.client.id] = clientMatters;
          });
          const list = [];
          Object.keys(clientMap).forEach(clientId => {
            const client = angular.copy(clientMap[clientId]);
            // find all matters that match the filter
            const matters = (mattersByClientId[clientId] || []).filter(matter => includeMatter(matter, me, options));
            // only include this client if there are matters or if we are requesting all (to account for orphans)
            if (matters.length || options.scope == 'all') {
              if (options.matters != 'exclude') {
                client.matters = matters;
              }
              list.push(client);
            }
          });
          return list;
        }));
      }
      return _state.clientLists[key];
    });
  }
  function listTabledClients() {
    return userService.me().then(async me => {
      if (!_state.tabledClientLists.length) {
        const query = {
          scope: 'all',
          status: 'active'
        };
        const {
          data
        } = await fetchPagedClients(query);
        _state.tabledClientLists = data;
      }
      return _state.tabledClientLists;
    });
  }
  function fetchPagedClients(query) {
    const path = `${domain}/clients/options`;
    return $http.get(path, {
      params: query
    });
  }
  function getClient(id, options = {}) {
    const request = options;
    request.id = id;
    return leanLawApi.getClient(request).then(client => new Client().load(client, true).then(obj => {
      // Only sync into the client map if it's already loaded/loading elsewhere -
      // opening one client must not itself trigger an unpaged whole-firm fetch.
      if (_state.clientMapPromise) {
        loadClientMap().then(clientMap => {
          clientMap[obj.id] = obj;
        });
      }
      return obj;
    }));
  }
  function createClient(create) {
    return leanLawApi.createClient({
      body: create
    }).then(created => loadClientMap().then(clientMap => {
      onboardService.reload();
      invalidate(true, false);
      return new Client().load(created).then(client => {
        clientMap[client.id] = client;
        return client;
      });
    }));
  }
  function listMatters(options = {}) {
    return userService.me().then(me => {
      const key = optionKey(options);
      if (!_state.matterLists[key]) {
        _state.matterLists[key] = loadMatterMap(options).then(matterMap => {
          const list = [];
          Object.keys(matterMap).forEach(id => {
            const matter = matterMap[id];
            if (includeMatter(matter, me, options)) {
              list.push(matter);
            }
          });
          return list;
        });
      }
      return _state.matterLists[key];
    });
  }
  function listMattersForClient(clientId, options) {
    if (!clientId) return [];
    return listMatters(options).then(matters => matters.filter(matter => matter.client.id === clientId));
  }
  function listClientsForMatters(matters, options) {
    return loadClientMap().then(clientMap => {
      const clientIds = {};
      matters.forEach(matter => {
        if (!clientIds[matter.client.id]) {
          clientIds[matter.client.id] = [];
        }
        clientIds[matter.client.id].push(matter);
      });
      return Object.keys(clientIds).map(id => {
        const client = angular.copy(clientMap[id]);
        client.matters = clientIds[id];
        return client;
      });
    });
  }
  function getMatter(id) {
    // Opening one matter must not itself trigger an unpaged whole-firm fetch -
    // only sync into the matter map if it's already loaded/loading elsewhere.
    if (!_state.matterMapPromise) {
      return leanLawApi.getMatter({
        id
      }).then(matter => new Matter().load(matter, true));
    }
    return loadMatterMap().then(matterMap => {
      const existing = matterMap[id];
      if (!existing) {
        // This should never really happen (getting a matter that we don't already know about)
        return leanLawApi.getMatter({
          id
        }).then(matter => new Matter().load(matter, true).then(obj => {
          matterMap[id] = obj;
          return obj;
        }));
      }
      return leanLawApi.getMatter({
        id
      }).then(matter => existing.load(matter, true));
    });
  }
  function createMatter(create) {
    // special case: if we created a matter for an orphaned client
    // in which case we need to invalidate clientMapPromise. For now
    // just invalidating it every time
    _state.clientMapPromise = null;
    const body = {
      ...create,
      // not passing the whole client object because it causes problems
      client: {
        id: create.client.id
      }
    };
    return leanLawApi.createMatter({
      body
    }).then(created => loadMatterMap().then(matterMap => {
      onboardService.reload();
      invalidate(true, true);
      return new Matter().load(created, true).then(matter => {
        matterMap[matter.id] = matter;
        return matter;
      });
    }));
  }
  function loadMatterMap(options = {}) {
    return userService.me().then(me => {
      if (!_state.matterMapPromise) {
        // Always load ALL matters (including secondary) in the main cache
        const query = {
          status: 'active',
          scope: 'all',
          showSecondarySplitMatter: true
        };
        _state.matterMapPromise = leanLawApi.listMatters(query).then(list => {
          const matterMap = {};
          return $q.all(list.matters.map(m => new Matter().load(m).then(matter => {
            matter.$access = {
              me: true
            };
            matterMap[matter.id] = matter;
          }))).then(() => matterMap);
        });
      }
      const key = mapKeyFromOptions(me, options);
      if (!key) {
        return _state.matterMapPromise;
      }
      return _state.matterMapPromise.then(matterMap => {
        if (!_state.scopeLoadedMap[key]) {
          const query = loadOptions(me, options);
          _state.scopeLoadedMap[key] = leanLawApi.listMatters(query).then(list => $q.all(list.matters.map(m => {
            const existing = matterMap[m.id];
            if (existing) {
              existing.$access[key] = true;
              return $q.resolve(true);
            }
            return new Matter().load(m).then(matter => {
              matter.$access = {};
              matter.$access[key] = true;
              matterMap[matter.id] = matter;
            });
          })).then(() => true));
        }
        return _state.scopeLoadedMap[key].then(() => matterMap);
      });
    });
  }
  function mapKeyFromOptions(me, options) {
    return requestForMe(me, options) && (!options.status || options.status === 'active') ? '' : `|${options.scopeAccount || 'undefined'}|${options.scope}`;
  }
  function requestForMe(me, options) {
    return (!options.scopeAccount || options.scopeAccount === 'me' || options.scopeAccount === me.id) && (!options.scope || options.scope.indexOf('originating') < 0 && options.scope.indexOf('reviewer') < 0);
  }
  function loadOptions(me, options) {
    // This is a bit complicated because when loading matters we are normally doing it for scopeAccount=me which
    // returns matters with an access property that we can filter on later. However, if we are loading scopeAccount != me,
    // then we don't have an access property that we can use to filter, so we need to do a server request for that.
    const result = {
      status: options.status || 'active',
      showSecondarySplitMatter: true // Always load all matters in the cache
    };
    if (requestForMe(me, options)) {
      result.scope = 'all';
    } else {
      result.scope = options.scope;
      result.scopeAccount = options.scopeAccount;
    }
    return result;
  }
  function loadClientMap() {
    if (!_state.clientMapPromise) {
      const query = {
        scope: 'all',
        status: 'active',
        matters: 'exclude',
        contact: 'include'
      };
      _state.clientMapPromise = leanLawApi.listClients(query).then(list => {
        const clientMap = {};
        return $q.all(list.clients.map(c => new Client().load(c).then(client => {
          clientMap[client.id] = client;
        }))).then(() => clientMap);
      });
    }
    return $q.resolve(_state.clientMapPromise);
  }
  function invalidate(client, matter) {
    if (client) {
      _state.clientLists = {};
    }
    if (matter) {
      _state.matterLists = {};
      _state.scopeLoadedMap = {};
    }
  }
  function hydrate() {
    return $q.all([loadMatterMap(), loadClientMap()]);
  }
  const QBOClients = [];
  async function listAccountingClient(list, type) {
    const accountingClients = await leanLawApi.listAccountingClients();
    accountingClients.clients.map(client => QBOClients.push(client));
    if (type === 'clients') validateClient(list);
    if (type === 'matters') validateMatters(list);
  }
  function validateMatters(matters) {
    return matters.map(matter => {
      const {
        client
      } = matter;
      if (client.accounting) {
        const qbClient = QBOClients.find(c => c.id === client.accounting.id);
        client.accounting.gone = !qbClient;
        if (matter.accounting && !client.billing.billClient) {
          matter.accounting.gone = !(qbClient && qbClient.clients && qbClient.clients.find(m => m.id === matter.accounting.id));
        }
      }
      return matter;
    });
  }
  function validateClient(clientLists) {
    return clientLists.map(client => {
      let qbClient = {};
      const clientAccounting = client.client ? client.client.accounting : client.accounting;
      if (clientAccounting) {
        qbClient = QBOClients.find(c => c.id === clientAccounting.id);
        clientAccounting.gone = !qbClient;
      }
      if (!client.matters || client.billing.billClient) return;
      client.matters.forEach(matter => {
        const matterAccounting = matter.matter ? matter.matter.accounting : matter.accounting;
        if (matterAccounting) {
          matterAccounting.gone = !(qbClient && qbClient.clients && qbClient.clients.find(m => m.id === matterAccounting.id));
        }
      });
      return client;
    });
  }
  return {
    hydrate,
    refresh,
    validateMatters,
    validateClient,
    listAccountingClient,
    QBOClients,
    // clients
    listClients,
    createClient,
    getClient,
    // tabled clients
    listTabledClients,
    // matters
    listMatters,
    createMatter,
    getMatter,
    listMattersForClient,
    listClientsForMatters
  };
}]);